import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { paymentsAPI, ordersAPI, authAPI } from '../services/api';
import { ga } from '../services/ga';
import { useT } from '../services/i18n';

const MAX_POLLS = 12;

const PaymentReturnPage = () => {
  const [params] = useSearchParams();
  const orderId = params.get('order_id');
  const { t } = useT();
  const user = authAPI.getCurrentUser();
  const navigate = useNavigate();
  const [state, setState] = useState('checking');
  const [orderNumber, setOrderNumber] = useState('');
  const [retrying, setRetrying] = useState(false);
  const stopped = useRef(false);

  useEffect(() => {
    if (!orderId) { setState('error'); return undefined; }
    stopped.current = false;
    (async () => {
      for (let i = 0; i < MAX_POLLS && !stopped.current; i += 1) {
        try {
          const r = await paymentsAPI.sumupConfirm(orderId);
          setOrderNumber(r.order_number || '');
          if (r.status === 'paid') { ga.purchase(r.analytics); setState('paid'); return; }
          if (r.status === 'failed' || r.status === 'mismatch') { setState('failed'); return; }
        } catch (e) {
          if (e.response?.status === 404) { setState('error'); return; }
        }
        await new Promise((res) => setTimeout(res, 2000));
      }
      if (!stopped.current) setState('pending');
    })();
    return () => { stopped.current = true; };
  }, [orderId]);

  const retry = async () => {
    setRetrying(true);
    try {
      if (user) {
        const r = await paymentsAPI.sumupCheckout(orderId);
        window.location.assign(r.checkout_url);
      } else {
        navigate('/connexion');
      }
    } catch (e) {
      setRetrying(false);
      setState('failed');
    }
  };

  const goOrders = async () => {
    if (!user) { navigate('/connexion'); return; }
    try { const r = await ordersAPI.customerPaymentLink(orderId); navigate(`/payer/${r.token}`); } catch { navigate('/mes-commandes'); }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <main className="max-w-2xl mx-auto px-4 py-12" data-testid="payment-return-page">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          {state === 'checking' && (
            <div data-testid="payment-return-checking">
              <Loader2 className="w-10 h-10 animate-spin text-orange-500 mx-auto mb-4" />
              <h1 className="text-xl font-bold text-gray-900">{t('payret.checking')}</h1>
              <p className="text-sm text-gray-500 mt-2">{t('payret.checkingHint')}</p>
            </div>
          )}
          {state === 'paid' && (
            <div data-testid="payment-return-paid">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"><CheckCircle className="w-10 h-10 text-green-500" /></div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('payret.paidTitle')}</h1>
              <p className="text-gray-600 mb-6">{t('payret.paidBody')}</p>
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-500">{t('checkout.orderNumber')}</p>
                <p data-testid="payment-return-order-number" className="text-lg font-mono font-bold text-gray-900">{orderNumber || orderId.slice(0, 8).toUpperCase()}</p>
              </div>
              <div className="flex gap-3 justify-center">
                <Link to="/mes-commandes"><Button data-testid="payment-return-orders" className="bg-orange-500 hover:bg-orange-600">{t('checkout.viewOrders')}</Button></Link>
                <Link to="/"><Button variant="outline" data-testid="payment-return-home">{t('common.continueShopping')}</Button></Link>
              </div>
            </div>
          )}
          {(state === 'failed' || state === 'pending') && (
            <div data-testid={`payment-return-${state}`}>
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6"><AlertTriangle className="w-10 h-10 text-amber-500" /></div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{t(state === 'failed' ? 'payret.failedTitle' : 'payret.pendingTitle')}</h1>
              <p className="text-gray-600 mb-6">{t(state === 'failed' ? 'payret.failedBody' : 'payret.pendingBody')}</p>
              {orderNumber && <p className="text-sm text-gray-500 mb-6">{t('checkout.orderNumber')} <span className="font-mono font-semibold text-gray-800">{orderNumber}</span></p>}
              <div className="flex gap-3 justify-center flex-wrap">
                <Button data-testid="payment-return-retry" onClick={retry} disabled={retrying} className="bg-orange-500 hover:bg-orange-600">
                  {retrying ? <Loader2 size={16} className="mr-2 animate-spin" /> : <RefreshCw size={16} className="mr-2" />}{t('payret.retry')}
                </Button>
                <Button variant="outline" data-testid="payment-return-later" onClick={goOrders}>{t('payret.later')}</Button>
              </div>
            </div>
          )}
          {state === 'error' && (
            <div data-testid="payment-return-error">
              <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-4" />
              <h1 className="text-xl font-bold text-gray-900 mb-2">{t('payret.errorTitle')}</h1>
              <Link to="/mes-commandes" className="text-orange-600 font-medium hover:underline">{t('checkout.viewOrders')}</Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PaymentReturnPage;
